import '@assistant-ui/react/styles/index.css';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, Outlet, useLocation, useSearchParams } from 'react-router-dom';

import { useAuthenticator } from '@aws-amplify/ui-react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  SvgIconTypeMap,
  Tooltip,
} from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import * as Sentry from '@sentry/react';
import { signInWithRedirect } from 'aws-amplify/auth';

import NavbarItemWithBadge from '../../components/NavbarItemWithBadge/NavbarItemWithBadge';
import ThemeToggle from '../../components/ThemeToggle/ThemeToggle';
import { SupportChatWidget } from '../../components/chat';
import { navbarItems, navbarNotificationsItemWithBadge } from '../../routes/navbar';
import bidLogo from '../../assets/images/bid-logo.png';

import styles from './styles';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 60;

// Quick hack: blocked user IDs
const BLOCKED_USER_IDS = ['23140872-2001-703a-898b-0fad3b627f43', 'f3449802-30a1-70c7-7c4d-676c9c2c0c05'];

function RootLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const hideMenu = searchParams.get('hideMenu') === 'true';
  const [isOpen, setIsOpen] = useState(false);
  const [isNavbarCollapsed, setIsNavbarCollapsed] = useState(false); // Desktop navbar collapsed state
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    // Set all sections to be collapsed by default
    [t('navbar.evaluation')]: true,
    [t('navbar.research')]: true,
    [t('navbar.marketIntelligence')]: true,
    'Deal Assessment': true,
  });

  // Auto-expand Evaluation section when on DFM page
  useEffect(() => {
    if (location.pathname === '/evaluation/dfm') {
      setCollapsedSections(prev => ({
        ...prev,
        [t('navbar.evaluation')]: false,
      }));
    }
  }, [location.pathname, t]);
  const { user, authStatus, signOut } = useAuthenticator();

  const hasCompanyParamId = searchParams.get('companyId') ? true : false;

  // Quick hack: Block specific user IDs
  useEffect(() => {
    if (user && user.userId && BLOCKED_USER_IDS.includes(user.userId)) {
      window.location.replace('/access-denied');
      return;
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      let username: string;

      if (user.username.includes('GoogleWorkspace_')) {
        username = user.username.replace('GoogleWorkspace_', '');
      } else {
        username = user.username;
      }
      console.log('User is authenticated. Setting Sentry user: ', username);
      console.log('User is authenticated. Setting Sentry userID: ', user.userId);

      Sentry.setUser({
        name: username,
        email: username,
      });
    } else {
      console.log('User is not authenticated');
      Sentry.setUser(null);
    }
  }, [user]);

  const handleAuth = async () => {
    if (authStatus === 'unauthenticated') {
      try {
        await signInWithRedirect({
          provider: {
            custom: 'GoogleWorkspace',
          },
        });
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error during sign-in:', error);
      }
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error signing out: ', error);
    }
  };

  const handleDrawerToggle = () => {
    setIsOpen(!isOpen);
  };

  const toggleSection = (sectionKey: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const renderIcon = (
    Icon: OverridableComponent<SvgIconTypeMap<{}, 'svg'>> & {
      muiName: string;
    }
  ) => {
    return <Icon sx={styles.navbarIcon} />;
  };

  const drawer = (
    <Box sx={styles.drawerContent}>
      <Box sx={styles.drawerHeader}>
        {!isNavbarCollapsed && (
          <Box sx={styles.drawerLogoWrapper}>
            <Link component={RouterLink} to="/" style={{ textDecoration: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <img src={bidLogo} alt="BID Equity" style={{ height: '32px', width: 'auto' }} />
                <Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '18px',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.2,
                    }}
                  >
                    Cortex
                  </Box>
                  <Box
                    sx={{
                      fontFamily: 'var(--font-data)',
                      fontSize: '10px',
                      fontWeight: 500,
                      color: 'var(--text-tertiary)',
                      letterSpacing: '0.03em',
                      textTransform: 'uppercase',
                      lineHeight: 1,
                      marginTop: '2px',
                    }}
                  >
                    Data-driven PE investments
                  </Box>
                </Box>
              </Box>
            </Link>
          </Box>
        )}
        {/* Desktop collapse button */}
        <IconButton
          onClick={() => setIsNavbarCollapsed(!isNavbarCollapsed)}
          sx={{
            display: { xs: 'none', sm: 'flex' },
            mx: 'auto', // Always center the button
            color: 'var(--text-secondary)',
            '&:hover': {
              color: 'var(--text-primary)',
              bgcolor: 'var(--surface-overlay)',
            },
          }}
        >
          {isNavbarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
        {/* Mobile close button */}
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            display: { sm: 'none' },
            ml: 'auto',
            color: 'var(--text-secondary)',
            '&:hover': {
              color: 'var(--text-primary)',
              bgcolor: 'var(--surface-overlay)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={styles.navigationList}>
        {navbarItems.map((item, index) => {
          const isActive = location.pathname.startsWith(item.path!);
          const isCollapsed = collapsedSections[item.text || ''] || false;
          const hasSubItems = item.subItems && item.subItems.length > 0;

          return (
            <div key={index}>
              <Link component={RouterLink} to={`${item.path!}${location.search}`} underline="none" color="inherit">
                <ListItemButton
                  sx={{
                    ...(isNavbarCollapsed ? styles.listItemButtonCollapsed : styles.listItemButton),
                    ...(isActive &&
                      (isNavbarCollapsed
                        ? styles.listItemButtonCollapsed['&.active']
                        : styles.listItemButton['&.active'])),
                  }}
                  onClick={
                    hasSubItems
                      ? e => {
                          // Allow navigation to main path, then toggle section
                          setTimeout(() => toggleSection(item.text || ''), 0);
                        }
                      : undefined
                  }
                >
                  {item?.icon && <item.icon sx={isNavbarCollapsed ? styles.navbarIconCollapsed : styles.navbarIcon} />}
                  {!isNavbarCollapsed && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        fontWeight: isActive ? 600 : 500,
                      }}
                    />
                  )}
                  {hasSubItems && !isNavbarCollapsed && (
                    <Box
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        bgcolor: isCollapsed ? 'var(--text-placeholder)' : 'var(--brand-coral)',
                        transition: 'all 0.2s ease',
                        ml: 'auto',
                      }}
                    />
                  )}
                </ListItemButton>
              </Link>
              {hasSubItems && !isCollapsed && !isNavbarCollapsed && (
                <List component="div" disablePadding>
                  {item.subItems!.map((subItem, subIndex) => {
                    const isDisabled = item.text === t('navbar.evaluation') && !hasCompanyParamId;
                    const isSubActive = location.pathname.startsWith(subItem.path);

                    // Apply menu item renaming
                    let displayText = subItem.text;
                    if (subItem.text === t('navbar.assessment')) displayText = 'Company';
                    if (subItem.text === t('navbar.techAssessment')) displayText = 'Product';
                    if (subItem.text === t('navbar.customerSegment')) displayText = 'Customers';
                    if (subItem.text === t('navbar.companyFiles')) displayText = 'Files';
                    // Note: Buy & Build and DD Findings are already renamed in navbar.ts

                    return (
                      <Box
                        key={subIndex}
                        sx={{
                          ...(isDisabled && { pointerEvents: 'none' }),
                        }}
                      >
                        <Link
                          component={RouterLink}
                          to={`${subItem.path}${location.search}`}
                          underline="none"
                          color={isDisabled ? 'text.disabled' : 'inherit'}
                          style={isDisabled ? { cursor: 'not-allowed' } : {}}
                        >
                          <ListItemButton
                            sx={{
                              ...styles.drawerListItemButton,
                              ...(isSubActive &&
                                !isDisabled && {
                                  bgcolor: 'var(--surface-overlay)',
                                  color: 'var(--text-primary)',
                                  fontWeight: 500,
                                  borderLeftColor: 'var(--brand-coral)',
                                }),
                            }}
                            disabled={isDisabled}
                          >
                            <Box sx={styles.drawerIconWrapper}>
                              <Box
                                sx={{
                                  width: 3,
                                  height: 3,
                                  bgcolor: 'currentColor',
                                  borderRadius: '50%',
                                  opacity: 0.6,
                                }}
                              />
                            </Box>
                            <ListItemText
                              primary={displayText}
                              primaryTypographyProps={{
                                fontSize: '0.8rem',
                              }}
                            />
                          </ListItemButton>
                        </Link>
                      </Box>
                    );
                  })}
                </List>
              )}
            </div>
          );
        })}
        <NavbarItemWithBadge
          {...navbarNotificationsItemWithBadge}
          location={location.search}
          isCollapsed={isNavbarCollapsed}
        />
      </List>

      <Box sx={{ mt: 'auto', p: isNavbarCollapsed ? 1 : 2 }}>
        {!isNavbarCollapsed && <Divider sx={{ mb: 2 }} />}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: isNavbarCollapsed ? 'center' : 'flex-start',
          }}
        >
          <ThemeToggle collapsed={isNavbarCollapsed} />
          <Tooltip title="Logout" placement={isNavbarCollapsed ? 'right' : 'top'}>
            <IconButton
              onClick={handleSignOut}
              size={isNavbarCollapsed ? 'small' : 'medium'}
              sx={{
                backgroundColor: '#FF564F',
                color: '#FFFFFF',
                border: '1px solid rgba(255, 86, 79, 0.12)',
                padding: isNavbarCollapsed ? '6px' : '8px',
                '&:hover': {
                  backgroundColor: '#E64841',
                  color: '#FFFFFF',
                  transform: 'scale(1.05)',
                  boxShadow: '0 2px 8px rgba(255, 86, 79, 0.25)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <LogoutIcon sx={{ fontSize: isNavbarCollapsed ? 18 : 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  if (!user) {
    handleAuth();
    return (
      <Box sx={styles.notAuthenticatedWrapper}>
        <CircularProgress color="error" size={50} />
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      {!hideMenu && (
        <Box
          component="nav"
          sx={{
            ...styles.wrapper,
            width: { sm: isNavbarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH },
            transition: 'width 0.3s ease',
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open navbar"
            edge="start"
            onClick={handleDrawerToggle}
            size="small"
            sx={styles.menuIconButton}
          >
            <MoreVertIcon />
          </IconButton>

          <Drawer
            variant="temporary"
            open={isOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              ...styles.mobileDrawer,
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: DRAWER_WIDTH,
                bgcolor: 'var(--surface-raised)',
                borderRight: '1px solid var(--border-subtle)',
                boxShadow: 'none',
              },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              ...styles.desktopDrawer,
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: isNavbarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH,
                transition: 'width 0.3s ease',
                overflowX: 'hidden',
                bgcolor: 'var(--surface-raised)',
                borderRight: '1px solid var(--border-subtle)',
                boxShadow: 'none',
              },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      )}
      <Box
        component="main"
        sx={{
          ...styles.mainContentWrapper,
          width: hideMenu ? '100%' : { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          transition: 'all 0.3s ease',
        }}
      >
        <Outlet />
      </Box>
      <SupportChatWidget />
    </Box>
  );
}

export default RootLayout;
